import { useLoading } from 'react-simplikit';

import AMP_KEYS from '@/configs/amp-track-keys';
import { errorHandler } from '@/error-handler';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import service from '@/services/index';
import { usePopup } from '@/stores/pure/PopupStore';
import { useToast } from '@/stores/pure/ToastStore';
import { track } from '@/utils/track/track';

type Props = {
  alias?: string;
  mode: EDIT_MODE;
  initChange: () => void;
};

const useWorkflowDetail = (props: Props) => {
  const wr = useWorkspaceRouter();
  const addToast = useToast();
  const setPopup = usePopup();
  const [isLoading, startLoading] = useLoading();

  const onPublish = async (
    createParams: CreateWorkflowParams,
    callback: (workflow: Workflow) => void,
  ) => {
    if (props.mode === 'CREATE') {
      try {
        const res = await service.workflow.createWorkflow(createParams);

        // Track workflow setup complete event
        const triggerCount =
          'realtime' in createParams
            ? createParams.realtime?.filterset
              ? Object.keys(createParams.realtime.filterset).length
              : 0
            : 0;

        track(AMP_KEYS.WORKFLOW_SETUP_COMPLETE, {
          workflow_alias: res.alias,
          workflow_type: createParams.type,
          trigger_count: triggerCount,
        });

        addToast('Workflow created', 'success');
        callback(res);
      } catch (e: any) {
        addToast(errorHandler(e), 'fail');
        return;
      }
    }
    if (props.mode === 'EDIT') {
      if (!props.alias) return;
      await service.workflow.updateWorkflow({
        alias: props.alias,
        ...createParams,
      });
      props.initChange();
      addToast('Notification updated', 'success');
    }
  };

  const onTest = async (params: CreateWorkflowParams) => {
    await startLoading(service.workflow.previewWorkflow(params));
    addToast('Test notification sent', 'success');
  };

  const onDelete = async () => {
    setPopup({
      popupTheme: 'negative',
      type: 'confirm',
      title: 'Delete the notification',
      message: 'Are you sure you want to delete this notification?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        if (!props.alias) return;
        props.initChange();
        await startLoading(service.workflow.deleteWorkflow(props.alias));
        addToast('Notification deleted', 'success');
        wr.push('/workflow');
      },
    });
  };

  return {
    onPublish,
    onTest,
    onDelete,
    isLoading,
  };
};

export default useWorkflowDetail;
